import React, { useMemo, useState } from "react";
import {
	Row,
	OrderText,
	DescriptionText,
	BlockContainer,
	BlockTitle,
	ProductDescription,
	ProductDescriptionContainer,
	Title,
	ContainerDescription,
	IconContainer,
	CancelContainer,
	Action,
	ConsignContainer,
	ModalContainer,
	GoBackText,
} from "./ConfirmationPage.style";
import { ActivityIndicator, Pressable, StyleSheet, View } from "react-native";
import { QuotationSummary } from "@atomic/organisms/Cart/QuotationSummary";
import { Receipt, Title16, Title13 } from "@stylesheets";
import { OrderViewModel, POSScreens, HomeScreens, PointOfSaleActions, ConfirmationPageActions, ComingSoonViewModel, cancelableOrderStates } from "@modules";
import {
	CashSystem,
	IPaymentTypes,
	OfferTemplateWithdrawalType as WithdrawalType,
	OrderItem,
	OrderState,
	PaymentMethod,
	OrderItemFormula,
	BrandTheme,
} from "@foodi/core";
import { Colors, Spacing } from "@constants";
import { getLabelFromState, getDecodedId, getLabelArticleBaking, htmlStringToPdfDownload } from "@utils";
import { useDispatch, useSelector } from "react-redux";
import { NavigationProp } from "@react-navigation/native";
import { GlobalState, ModalActions } from "@redux";
import { I18n } from "react-redux-i18n";
import { Button, CloseXButton, ModalTemplate, Tag } from "@atomic";
import { TestIDs } from "@utils";
import { useDevices } from "@hooks";
import { Column, WarningMessage, WarningMessageContainer } from "@atomic/organisms/Cart/Cart.style";
import { Information, WarningOrange, WarningTriangle } from "@assets";
import { CancellationReceiptButtons, ReceiptButtons } from "@modules/pointsOfSale/screens/ConfirmationPage/ReceiptButtons";

interface IProps {
	order: any;
	date: string;
	cancelDate: string;
	navigation?: NavigationProp<any>;
	idPos: number;
	cleaningDate: string;
}

export const ClickAndCollectContent: React.FC<IProps> = React.memo(({ order, date, cleaningDate, cancelDate, navigation, idPos }) => {
	const dispatch = useDispatch();
	const [isMobile] = useDevices();
	const brandTheme = useSelector((state: GlobalState) => state.brandTheme.brandTheme);
	const styles = useMemo(() => _styles(isMobile, brandTheme), [isMobile, brandTheme]);

	const orderVM = new OrderViewModel(dispatch, "");
	const comingSoonViewModel = new ComingSoonViewModel();

	const [loading, setLoading] = useState<boolean>(false);

	const [hasContainer, setHasContainer] = useState<boolean>(false);

	const currentDate = Date.now();
	const dateCancelDate = new Date(order?.cancelableUntil).valueOf();
	const canceledState = order?.state === OrderState.CANCELLED;
	const isRefusedState = order?.state === OrderState.REFUSED;
	const isReadyState = order?.state === OrderState.READY;
	const withdrawalType: WithdrawalType | undefined = order?.offer?.offerTemplate?.withdrawalType;
	const isSeatClick = withdrawalType === WithdrawalType.POS_CLICK_SERVE;
	const isInstantCC = withdrawalType === WithdrawalType.INSTANT_CLICK_COLLECT;
	const isTableService = withdrawalType === WithdrawalType.TABLE_SERVICE;
	const isClickPickup = withdrawalType === WithdrawalType.CLICK_AND_PICK_UP;
	const isCancelableState = cancelableOrderStates.includes(order?.state);
	const isInAdvanceOrder = comingSoonViewModel.isInAdvanceOrder(order);
	const isInAdvanceEdenredOrder = order?.paymentMethod === PaymentMethod.Edenred && isInAdvanceOrder;

	const idCashSystem = useSelector((state: GlobalState) => state.auth?.userInfo?.idCashSystem);

	const isCatCSI = isTableService && idCashSystem === CashSystem.CSI;

	const { isSchool, idGuest } = useSelector((state: GlobalState) => state.auth?.userInfo || {});

	let canCancel =
		isCancelableState &&
		!isCatCSI &&
		(isSeatClick || isInstantCC || isTableService || (dateCancelDate > currentDate && order?.cancelableUntil !== null)) &&
		!isInAdvanceEdenredOrder;

	if (isClickPickup && dateCancelDate > currentDate && order?.cancelableUntil !== null && !isInAdvanceEdenredOrder) {
		canCancel = order?.offer?.published;
	}

	const tableNumber: number | undefined = order?.tableNumber;

	const id = getDecodedId(order?.id);

	const handleCancel = () => {
		dispatch(ModalActions.setOpen(true));
	};

	const handleCloseModal = () => {
		dispatch(ModalActions.setOpen(false));
	};

	const handleCancelOrder = async () => {
		if (order && canCancel) {
			setLoading(true);

			try {
				const res = await orderVM.cancelOrder({
					idOrder: order?.id,
					newState: OrderState.CANCELLED,
					paymentMethod: order?.paymentMethod,
				});

				if (res) {
					setLoading(false);
				}
				dispatch(PointOfSaleActions.setNewOrder(true));
				dispatch(ModalActions.setOpen(false));

				dispatch(
					ConfirmationPageActions.setConfirmationPageParams({
						id: id?.split(":")?.[1],
						orderId: order?.id,
						isFirstTime: false,
						idPos: -1,
						offerSlot: null,
					}),
				);

				navigation?.navigate(HomeScreens.POINT_OF_SALE_STACK, {
					screen: POSScreens.CONFIRMATION_PAGE,
				});
			} catch (err) {
				setLoading(false);
				dispatch(ModalActions.setOpen(false));
				console.error("Cancellation failed:", err);
			}
		}
	};

	const customOrder =
		order?.paymentMethod === IPaymentTypes.OnSite && !isCatCSI
			? {
					admission: order?.admission,
					subsidies: order?.subsidies,
					fee: order?.fee,
			  }
			: order;

	const infoPayOnSite = isSchool ? I18n.t("restaurantDetail.cart.payOnSiteSchoolInfo") : I18n.t("restaurantDetail.cart.payOnSiteInfo");

	const orderId = withdrawalType === WithdrawalType.CONNECTED_LOCKERS ? id?.split(":")?.[1] : order?.idWithdrawal;

	const stateLabelColor = orderVM.getOrderLabelColor(order?.state);
	const formulaItems =
		order?.orderItemsFormula
			?.filter((f: OrderItemFormula) => f.stepNumber === null)
			?.map((f: OrderItemFormula) => ({
				...f,
				totalPriceWhenAdded: f.priceWhenAdded,
				subItems: order?.orderItemsFormula?.filter(
					(subI: OrderItemFormula) => subI.stepNumber !== null && subI.stepNumber >= 0 && subI?.orderItemFormulaParent?.id === f?.id,
				),
			})) || [];

	const totalPriceFormmated = `${order?.totalPrice?.amount?.replace(".", ",")}€`;
	return (
		<>
			<Row testID={TestIDs.home.confirmationPage.views.CCContainer}>
				{canceledState && (
					<Title testID={TestIDs.home.confirmationPage.views.titleCanceled} isMobile={isMobile}>
						{I18n.t("confirmationPage.yourOrderCanceled")}
					</Title>
				)}
			</Row>
			<Row>
				<OrderText isMobile={isMobile}>{`${I18n.t("confirmationPage.order")} #${orderId}  `}</OrderText>
				<OrderText bold isMobile={isMobile} labelColor={stateLabelColor}>
					{I18n.t(`homepage.comingSoon.${getLabelFromState(order?.state)}`)}
				</OrderText>
			</Row>
			{isInstantCC && !canceledState && (
				<Row maxWidth={800} isMobile={isMobile}>
					<DescriptionText isMobile={isMobile}>{I18n.t("confirmationPage.instantCCInfo")}</DescriptionText>
				</Row>
			)}
			{!canceledState &&
				(isSeatClick || isTableService ? (
					<>
						{isReadyState && <DescriptionText isMobile={isMobile}>{I18n.t("confirmationPage.dishServed")}</DescriptionText>}
						<Row>
							<DescriptionText isMobile={isMobile}>{I18n.t("confirmationPage.tableNumber")} : </DescriptionText>
							<DescriptionText bold isMobile={isMobile}>
								{tableNumber}
							</DescriptionText>
						</Row>
					</>
				) : (
					<>
						<DescriptionText isMobile={isMobile}>{I18n.t("confirmationPage.orderInformation")}</DescriptionText>
						<Row>
							<DescriptionText isMobile={isMobile}>
								{!isClickPickup
									? I18n.t("confirmationPage.withdrawalWindow")
									: order?.offer?.isRoomService
									? I18n.t("confirmationPage.withdrawalWindowClickPickUpRoomService")
									: I18n.t("confirmationPage.withdrawalWindowClickPickUp")}{" "}
								:{" "}
							</DescriptionText>
							<DescriptionText bold isMobile={isMobile}>
								{isClickPickup && !order?.extra?.pickupWithSlot ? `${date + " - " + order?.pickupPoint?.withdrawalSchedule}` : date}
							</DescriptionText>
						</Row>
						{!!cleaningDate && (
							<Row>
								<DescriptionText isMobile={isMobile}>{I18n.t("confirmationPage.cleaningTimeLabel")} : </DescriptionText>
								<DescriptionText bold isMobile={isMobile}>
									{cleaningDate}
								</DescriptionText>
							</Row>
						)}
						<Row>
							<DescriptionText isMobile={isMobile}>{I18n.t("confirmationPage.pickupLocation")} : </DescriptionText>
							<DescriptionText bold isMobile={isMobile}>
								{isClickPickup
									? `${order?.pickupPoint?.name} ${order?.pickupPoint?.description ? "- " + order?.pickupPoint?.description : ""}`
									: order?.withdrawLocation}
							</DescriptionText>
						</Row>
					</>
				))}
			<BlockContainer isMobile={isMobile}>
				<BlockTitle>{I18n.t("confirmationPage.yourOrder")}</BlockTitle>
				{[...formulaItems, ...(order?.orderItems || [])].map((p: OrderItem | OrderItemFormula) => {
					if (!hasContainer && p.containerLabelWhenAdded) {
						setHasContainer(true);
					}

					return (
						<React.Fragment key={p.id}>
							<Row>
								<ProductDescriptionContainer>
									<ProductDescription marginBottom={p.containerLabelWhenAdded ? 3 : 12} marginRight={5}>
										{p.quantity}x
									</ProductDescription>
									<Column>
										{/* @ts-ignore */}
										<ProductDescription marginBottom={!!p?.subItems?.length ? 8 : undefined}>{p.labelWhenAdded}</ProductDescription>
										{/* @ts-ignore */}
										{!!(p as any)?.subItems?.length &&
											(p as any).subItems.map((subP: any) => (
												<ProductDescription lineHeight={15} marginLeft={0}>
													- {`${subP.quantity}x `} {subP.labelWhenAdded}
												</ProductDescription>
											))}
										{p.containerLabelWhenAdded && (
											<ContainerDescription isMobile={isMobile} numberOfLines={2}>
												{I18n.t("restaurantDetail.cart.container")} {p.containerLabelWhenAdded}
											</ContainerDescription>
										)}
									</Column>
								</ProductDescriptionContainer>
								<Column>
									<ProductDescription marginBottom={p.containerLabelWhenAdded ? 3 : 12} alignSelf="flex-end">
										{String(Number(p?.priceWhenAdded?.amount) * p?.quantity)?.replace(".", ",")}€
									</ProductDescription>
									{p.containerPriceWhenAdded && (
										<ProductDescription alignSelf="flex-end">
											{String(Number(p.containerPriceWhenAdded?.amount) * p?.quantity)?.replace(".", ",")}€
										</ProductDescription>
									)}
								</Column>
							</Row>
							{!!p.chosenBaking && (
								<Row>
									<ProductDescription>
										<Tag isSelected text={I18n.t(`restaurantDetail.articleBaking.${getLabelArticleBaking(p.chosenBaking)}`)} />
									</ProductDescription>
								</Row>
							)}
						</React.Fragment>
					);
				})}
				{order && <QuotationSummary paymentMethod={order?.paymentMethod} quotation={customOrder} isCatCSI={isCatCSI} isSchool={order?.isSchool} />}
				<Row>
					<ProductDescription bold hasNoMaxWidth>
						{I18n.t("restaurantDetail.cart.total")}
						{order?.paymentMethod === IPaymentTypes.OnSite && ` - ${I18n.t("confirmationPage.paymentOnSite")}`}
					</ProductDescription>
					<ProductDescription bold>{totalPriceFormmated}</ProductDescription>
				</Row>
				{(order?.paymentMethod === IPaymentTypes.OnSite ||
					order?.paymentMethod === IPaymentTypes.Edenred ||
					order?.paymentMethod === IPaymentTypes.CreditCard ||
					order?.paymentMethod === IPaymentTypes.GooglePay ||
					order?.paymentMethod === IPaymentTypes.ApplePay) && (
					<WarningMessageContainer marginTop={0} backgroundColor={Colors.warningLightYellow} style={styles.warningMessageContainer}>
						<WarningTriangle color={Colors.warningDarkOrange} width={20} height={20} />
						<WarningMessage style={styles.warningMessage}>
							{order?.paymentMethod === IPaymentTypes.OnSite ? infoPayOnSite : I18n.t("restaurantDetail.cart.noSubventions")}
						</WarningMessage>
					</WarningMessageContainer>
				)}
				{isInAdvanceOrder && (
					<Row>
						<Information style={{ marginTop: 2 }} width={"40px"} />
						<ProductDescription hasNoMaxWidth marginLeft={8}>
							{I18n.t("confirmationPage.orderInAdvanceMessage")}
						</ProductDescription>
					</Row>
				)}
				{(orderVM.getDirectPaymentMethods().includes(order?.paymentMethod) || order?.paymentMethod === IPaymentTypes.Edenred) && (
					<ReceiptButtons
						order={order}
						brandTheme={brandTheme}
						isMobile={isMobile}
						idCashSystem={idCashSystem}
						seeReceiptTitle={I18n.t("confirmationPage.receipt.seeReceipt")}
						downloadReceiptTitle={I18n.t("confirmationPage.receipt.downloadReceipt")}
					/>
				)}
			</BlockContainer>
			{order?.comment && order.comment.length > 0 && (
				<BlockContainer isMobile={isMobile} testID={TestIDs.home.confirmationPage.views.commentContainer}>
					<BlockTitle>{I18n.t("confirmationPage.comment")}</BlockTitle>
					<Title16 style={styles.comment}>{order.comment}</Title16>
					<Title16 bold color={Colors.gray1}>
						{I18n.t("confirmationPage.commentNotice")}
					</Title16>
				</BlockContainer>
			)}
			{(canceledState || isRefusedState) &&
				(orderVM.getDirectPaymentMethods().includes(order?.paymentMethod) || order?.paymentMethod === IPaymentTypes.Edenred) && (
					<CancellationReceiptButtons
						order={order}
						brandTheme={brandTheme}
						isMobile={isMobile}
						idCashSystem={idCashSystem}
						totalPrice={totalPriceFormmated}
						seeReceiptTitle={I18n.t("confirmationPage.receipt.seeCancelReceipt")}
						downloadReceiptTitle={I18n.t("confirmationPage.receipt.downloadCancelReceipt")}
					/>
				)}
			{canCancel && (
				<CancelContainer testID={TestIDs.home.confirmationPage.views.cancelContainer} isMobile={isMobile}>
					{isSeatClick || isInstantCC || isTableService ? (
						<Title16 testID={TestIDs.home.confirmationPage.views.cancelSCSOrderText}>{I18n.t("confirmationPage.cancelSCSOrder")}</Title16>
					) : (
						<>
							{isClickPickup ? (
								<Title16>{I18n.t("confirmationPage.cancelClickAndPickup")}</Title16>
							) : (
								<>
									<Title16>{I18n.t("confirmationPage.cancelOrderInfo")}</Title16>
									<Title16>{cancelDate}</Title16>
								</>
							)}
						</>
					)}

					<Action testID={TestIDs.home.confirmationPage.actions.clickCancellation} hasBorder marginTop={10} onPress={handleCancel}>
						<BlockTitle>{I18n.t("confirmationPage.cancel")}</BlockTitle>
					</Action>
				</CancelContainer>
			)}
			{hasContainer && (
				<ConsignContainer>
					<Title13 color={Colors.foodiBlack}>{I18n.t("restaurantDetail.container.infoMessage")}</Title13>
				</ConsignContainer>
			)}
			<ModalTemplate
				forwardTestID={TestIDs.home.confirmationPage.views.confirmCancellation}
				forwardCloseTestID={TestIDs.home.confirmationPage.actions.backCancellation}
				style={{ width: 414 }}
				closeXButtonStyle={styles.closeXButtonStyle}
				handleClose={handleCloseModal}
				closeOnClickOutside
			>
				<ModalContainer>
					<Row>
						<IconContainer>
							<WarningOrange />
						</IconContainer>
						<GoBackText>{I18n.t("confirmationPage.cancelConfirmation")}</GoBackText>
					</Row>
					<Action
						testID={TestIDs.home.confirmationPage.actions.cancelOrder}
						marginTop={34}
						onPress={handleCancelOrder}
						justifyContent={loading}
						backgroundColor={brandTheme.buttonActionColors?.backgroundColor || Colors.foodiDefault}
					>
						{loading ? (
							<>
								<ActivityIndicator style={styles.loadingStyle} color={brandTheme.buttonActionColors?.textColor || Colors.foodiBlack} />
								<BlockTitle hide>{I18n.t("confirmationPage.cancelOrder")}</BlockTitle>
							</>
						) : (
							<BlockTitle color={brandTheme.buttonActionColors?.textColor || Colors.foodiBlack}>
								{I18n.t("confirmationPage.cancelOrder")}
							</BlockTitle>
						)}
					</Action>
				</ModalContainer>
			</ModalTemplate>
		</>
	);
});

const _styles = (isMobile: boolean, brandTheme: BrandTheme) =>
	StyleSheet.create({
		loadingStyle: {
			position: "absolute",
			left: 0,
			right: 0,
			top: 0,
			bottom: 0,
			alignItems: "center",
			justifyContent: "center",
		},
		closeXButtonStyle: {
			top: -15,
			right: -5,
		},
		closeXButtonStyleReceiptModal: {
			top: -25,
			right: -5,
		},
		comment: {
			marginVertical: Spacing.XS,
		},
		receiptSection: {
			width: "100%",
			flexDirection: "row",
			justifyContent: "space-between",
			marginTop: 10,
		},
		viewReceiptButton: {
			backgroundColor: "FFF",
			borderWidth: 2,
			borderStyle: "solid",
			borderColor: brandTheme.buttonActionColors?.backgroundColor || Colors.foodiDefault,
			borderRadius: 8,
			width: "47%",
			height: 54,
		},
		downloadReceiptButton: {
			width: "47%",
			height: 54,
		},
		receiptModalDiv: {
			width: isMobile ? "100%" : 520,
			maxHeight: isMobile ? "100%" : "95vh",
		},
		downLoadContainerModal: {
			position: "absolute",
			right: 60,
			top: -30,
		},
		receipt: {
			marginTop: 10,
		},
		warningMessage: {
			fontFamily: "manrope-bold",
			color: Colors.warningDarkOrange,
			fontSize: 14,
		},
		warningMessageContainer: {
			borderRadius: 8,
			padding: 10,
		},
	});
